import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { AddressGroup } from '@classes/address-group';

@Injectable({
  providedIn: 'root',
})
export class AddressGroupsService {
  readonly addressGroupsListChanged = new Subject<AddressGroup[]>();
  private readonly baseUrl: string = environment.apiUrl + 'address-groups';
  private addressGroups: AddressGroup[];

  constructor(private http: HttpClient) {}

  getList(): Observable<AddressGroup[]> {
    return this.http.get<AddressGroup[]>(this.baseUrl).pipe(
      map((addressGroups) => {
        this.addressGroups = addressGroups.map((group) =>
          AddressGroup.fromObject(group),
        );
        this.addressGroups.sort((a, b) => a.name.localeCompare(b.name));
        this.addressGroupsListChanged.next([...this.addressGroups]);
        return [...this.addressGroups];
      }),
    );
  }

  put(addressGroup: AddressGroup): Observable<AddressGroup> {
    return this.http
      .put<AddressGroup>(this.baseUrl, this.toPayload(addressGroup, true))
      .pipe(
      tap((editedAddressGroup) => {
        editedAddressGroup = AddressGroup.fromObject(editedAddressGroup);
        this.addressGroups = this.addressGroups.map((group) =>
          group.id === editedAddressGroup.id ? editedAddressGroup : group,
        );
        this.addressGroupsListChanged.next([...this.addressGroups]);
      }),
      );
  }

  post(addressGroup: AddressGroup): Observable<AddressGroup> {
    return this.http.post<AddressGroup>(this.baseUrl, this.toPayload(addressGroup)).pipe(
      tap((createdAddressGroup) => {
        createdAddressGroup = AddressGroup.fromObject(createdAddressGroup);
        this.addressGroups.push(createdAddressGroup);
        this.addressGroupsListChanged.next([...this.addressGroups]);
      }),
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      tap(() => {
        this.addressGroups = this.addressGroups.filter((group) => group.id !== id);
        this.addressGroupsListChanged.next([...this.addressGroups]);
      }),
    );
  }

  private toPayload(addressGroup: AddressGroup, includeId: boolean = false) {
    const payload = {
      name: addressGroup.name,
      addressIds: addressGroup.addressIds,
      siteIds: addressGroup.siteIds,
      siteGroupIds: addressGroup.siteGroupIds,
    };

    return includeId ? { id: addressGroup.id, ...payload } : payload;
  }
}
